import type { CSSProperties, FocusEventHandler, ReactNode } from "react";
import { FOLD_MS } from "@libs/checkout-core";
import { FOLD_EASE } from "./motion";
import { CONTROL_HEIGHT, button, size, text } from "./ui";

/**
 * The checkout's layout pieces: the root frame the appearance lives on, the
 * stacks every section is spaced by, the one notice, and the fold that
 * closes the checkout around the payment's status once Pay is pressed.
 */

export { FOLD_EASE } from "./motion";

/** Flex column that takes the remaining height, so the pay controls at the
 *  end of the chain can sit at the bottom of whatever room the page gave. */
export const GROW: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
};

/** The pay controls: whatever is above may be short, these stay at the foot. */
export const TERMINAL: CSSProperties = {
  display: "grid",
  gap: "calc(var(--lc-space) * 1.75)",
  marginTop: "auto",
};

/** Either side of the paid checkout's status on its own page: nothing until
 *  the fold, then half the free room each. */
export const centreSpacer = (on: boolean): CSSProperties => ({
  flexGrow: on ? 1 : 0,
  transition: `flex-grow ${FOLD_MS}ms ${FOLD_EASE}`,
});

/** Pay while it cannot pay yet: the same button, stepped back. */
export const payWaiting: CSSProperties = {
  ...button.primary,
  cursor: "default",
  minHeight: CONTROL_HEIGHT,
  opacity: 0.5,
};

export function Stack({
  children,
  gap = 2.5,
  grow = false,
  style,
}: {
  children: ReactNode;
  gap?: number;
  grow?: boolean;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        ...(grow ? GROW : { display: "grid" }),
        gap: `calc(var(--lc-space) * ${gap})`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Notice({
  message,
  action,
}: {
  message: string;
  /** One thing to do about it, said after the message in its own colour. */
  action?: { label: string; onClick: () => void };
}) {
  return (
    <p
      role="alert"
      style={{
        background: "color-mix(in srgb, var(--lc-danger) 10%, transparent)",
        borderRadius: "var(--lc-radius-sm)",
        color: "var(--lc-danger)",
        fontSize: size.small,
        margin: 0,
        padding: "calc(var(--lc-space) * 1.5)",
      }}
    >
      {message}
      {action ? (
        <>
          {" "}
          <button
            type="button"
            onClick={action.onClick}
            style={{ ...button.link, color: "inherit", fontSize: "inherit" }}
          >
            {action.label}
          </button>
        </>
      ) : null}
    </p>
  );
}

export function Divider({ children }: { children: ReactNode }) {
  const line: CSSProperties = {
    borderTop: "var(--lc-border)",
    flex: "1 1 0",
  };
  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        gap: "var(--lc-space)",
        ...text.hint,
      }}
    >
      <span style={line} />
      <span>{children}</span>
      <span style={line} />
    </div>
  );
}

/**
 * Once pay is pressed, everything but the status folds away, so the sheet
 * around the checkout closes in on it. The row collapses to nothing while
 * its content fades; the content stays mounted (Stripe's fields confirm the
 * card, and a refused payment unfolds them as typed).
 */
export function Fold({
  folded,
  style,
  children,
}: {
  folded: boolean;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div
      aria-hidden={folded || undefined}
      data-lc-fold=""
      style={{
        display: "grid",
        gridTemplateRows: folded ? "0fr" : "1fr",
        opacity: folded ? 0 : 1,
        pointerEvents: folded ? "none" : undefined,
        transition: `grid-template-rows ${FOLD_MS}ms ${FOLD_EASE}, opacity ${folded ? 200 : 350}ms ease`,
      }}
    >
      {/* Clipped with room for focus rings: the negative margin gives the
          padding back, so unfolded it takes exactly its content's space. */}
      <div style={{ margin: -4, minHeight: 0, overflow: "hidden", padding: 4 }}>
        <div style={style}>{children}</div>
      </div>
    </div>
  );
}

/** A Stack's gap, closing (to `foldedGap`) along with the folds inside it. */
export const foldGap = (
  folded: boolean,
  gap: number,
  foldedGap = 0,
): CSSProperties => ({
  gap: `calc(var(--lc-space) * ${folded ? foldedGap : gap})`,
  // What the gap closes to, for foldedHeight() to read.
  ["--lc-fold-gap-to" as string]: String(foldedGap),
  transition: `gap ${FOLD_MS}ms ${FOLD_EASE}`,
});

/**
 * The document's height once the folds just set in motion have closed: its
 * height now (their transitions have not moved yet), less the folds' rows
 * and less what each folding Stack's gaps give up. Read the moment the fold
 * starts, so the frame can tell its page one height for the whole glide.
 */
export function foldedHeight() {
  const body = document.body;
  let height = Math.max(body.getBoundingClientRect().height, body.scrollHeight);
  for (const fold of document.querySelectorAll<HTMLElement>(
    '[data-lc-fold][aria-hidden="true"]',
  ))
    height -= fold.getBoundingClientRect().height;
  for (const stack of document.querySelectorAll<HTMLElement>(
    '[style*="--lc-fold-gap-to"]',
  )) {
    const style = getComputedStyle(stack);
    const to =
      parseFloat(style.getPropertyValue("--lc-space")) *
      parseFloat(stack.style.getPropertyValue("--lc-fold-gap-to"));
    const now = parseFloat(style.rowGap) || 0;
    const items = Array.from(stack.children).filter(
      (child) => getComputedStyle(child).display !== "none",
    ).length;
    if (Number.isFinite(to) && items > 1) height -= (now - to) * (items - 1);
  }
  return Math.max(1, Math.ceil(height));
}

/**
 * The one page to pay on, as a column: what heads it (the order, where no
 * host shows it), the one notice, then the body — the destination, whatever
 * the provider asks for, the payment — which folds in on the status once the
 * primary button is pressed. On its own page (`centred`) the folded body
 * settles in the middle of the room; in a frame or a sheet, the frame
 * shrinks around it instead. The engine's payment and a vendor's prepared
 * checkout both draw this column.
 */
export function CheckoutColumn({
  folded,
  centred = false,
  head,
  notice,
  onFocusCapture,
  children,
}: {
  folded: boolean;
  centred?: boolean;
  head?: ReactNode;
  notice: ReactNode;
  onFocusCapture?: FocusEventHandler<HTMLDivElement>;
  children: ReactNode;
}) {
  return (
    <div
      style={{ display: "grid", flex: "1 1 auto" }}
      onFocusCapture={onFocusCapture}
    >
      <Stack
        grow
        style={{
          ...foldGap(folded, 2.5),
          gridArea: "1 / 1",
        }}
      >
        {head}
        {/* The one place an error is shown. */}
        {notice}
        <div style={GROW}>
          <div style={centreSpacer(centred)} />
          <Stack
            grow
            style={{
              ...foldGap(folded, 2.5, 1.5),
              flexGrow: centred ? 0 : 1,
              transition: `gap ${FOLD_MS}ms ${FOLD_EASE}, flex-grow ${FOLD_MS}ms ${FOLD_EASE}`,
            }}
          >
            {children}
          </Stack>
          <div style={centreSpacer(centred)} />
        </div>
      </Stack>
    </div>
  );
}

/**
 * The payment's half of the body: the provider's card fields (the engine's
 * card island, or a vendor's secure fields), folding away once pressed but
 * kept mounted, and at the foot the one primary button and what follows it.
 */
export function PaymentArea({
  folded,
  card,
  children,
}: {
  folded: boolean;
  card: ReactNode;
  /** The terminal: the primary button, then anything under it. */
  children: ReactNode;
}) {
  return (
    <Stack gap={1.5} grow style={foldGap(folded, 1.5)}>
      <Fold folded={folded}>{card}</Fold>
      <div style={TERMINAL}>{children}</div>
    </Stack>
  );
}

/** The component root: the appearance lives here and nowhere else, so a
 *  partner's `update({ appearance })` is one re-render and no reload. */
export function Frame({
  variables,
  fonts,
  minHeight,
  children,
}: {
  variables: CSSProperties;
  fonts?: string | null;
  minHeight?: number | null;
  children: ReactNode;
}) {
  return (
    <div
      data-lissen-checkout=""
      style={{
        background: "var(--lc-background)",
        boxSizing: "border-box",
        color: "var(--lc-text)",
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        minHeight: minHeight ?? undefined,
        fontFamily: "var(--lc-font-family)",
        fontSize: "var(--lc-font-size)",
        gap: "calc(var(--lc-space) * 2)",
        width: "100%",
        ...variables,
      }}
    >
      <style>{`[data-lissen-checkout] input::placeholder{color:var(--lc-input-placeholder)}
[data-lissen-checkout] input:not([type=checkbox]),[data-lissen-checkout] select,[data-lissen-checkout] textarea{font-size:var(--lc-input-font-size)}
[data-lissen-checkout] input[type=checkbox]{appearance:none;-webkit-appearance:none;width:16px;height:16px;border:var(--lc-border-width) var(--lc-border-style) var(--lc-input-border);border-radius:4px;background:var(--lc-input-background);display:inline-grid;place-content:center;cursor:inherit}
[data-lissen-checkout] input[type=checkbox]::before{content:"";width:8px;height:4px;border:2px solid var(--lc-on-accent);border-top:0;border-right:0;transform:translateY(-1px) rotate(-45deg) scale(0)}
[data-lissen-checkout] input[type=checkbox]:checked{background:var(--lc-accent);border-color:var(--lc-accent)}
[data-lissen-checkout] input[type=checkbox]:checked::before{transform:translateY(-1px) rotate(-45deg) scale(1)}
[data-lissen-checkout] input[type=checkbox]:focus-visible,[data-lissen-checkout] .lc-saved-card:focus-within{outline:2px solid var(--lc-accent);outline-offset:2px}
@media (prefers-reduced-motion: reduce){[data-lc-fold]{transition:opacity 200ms ease!important}[data-lissen-checkout] *{transition-property:opacity,color,background-color,border-color!important}}`}</style>
      {fonts ? (
        <>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link
            rel="preconnect"
            href="https://fonts.gstatic.com"
            crossOrigin="anonymous"
          />
          <link rel="stylesheet" href={fonts} />
        </>
      ) : null}
      {children}
    </div>
  );
}
